import { useLayoutEffect, useRef, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import PieChartView from "../pages/Reports/ReportGraph/PieChartView";

// Width at which PieChartView (untouched) looks right: big donut, legend on
// the right. Narrower chats scale this down, wider ones scale it up a little.
const DESIGN_WIDTH = 640;
const MAX_SCALE = 1.15;
const MAX_ROWS_COLLAPSED = 8;

// PieChartView compares this header text exactly, so never rename it.
const KEEP_AS_IS = "Credit Card Amount breakdown ($)";

// "Cash Received (Cash total- Cash refunds) ($)" -> "Cash Received ($)".
// Only the pie legend gets the short text; the table keeps the full header.
const shortLegend = (label = "") =>
  label === KEEP_AS_IS ? label : label.replace(/\s*\([^)]*\btotal\b[^)]*\)/gi, "").trim();

// 100.32000000000001 -> "100.32", 12 -> "12", dates and text untouched
function fmtCell(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : v.toFixed(2);
  const s = String(v);
  return /^-?\d+\.\d+$/.test(s) ? Number(s).toFixed(2) : s;
}

/**
 * Renders children at a fixed design width and scales the whole block to the
 * space available. Apex lays the chart out once at DESIGN_WIDTH, so the donut
 * keeps the proportions of the real report page at every chat size.
 */
function FitToWidth({ children }) {
  const hostRef = useRef(null);
  const innerRef = useRef(null);
  const [m, setM] = useState({ hostW: DESIGN_WIDTH, innerH: 0 });

  useLayoutEffect(() => {
    const host = hostRef.current;
    const inner = innerRef.current;
    if (!host || !inner) return undefined;
    const measure = () => setM({ hostW: host.clientWidth, innerH: inner.offsetHeight });
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(host);
    ro.observe(inner);
    return () => ro.disconnect();
  }, []);

  const scale = Math.min(MAX_SCALE, Math.max(0.3, m.hostW / DESIGN_WIDTH));
  const left = Math.max(0, (m.hostW - DESIGN_WIDTH * scale) / 2);

  return (
    <Box
      ref={hostRef}
      sx={{ position: "relative", width: "100%", overflow: "hidden", height: m.innerH ? m.innerH * scale : undefined }}
    >
      <Box
        ref={innerRef}
        sx={{
          position: "absolute",
          top: 0,
          left,
          width: DESIGN_WIDTH,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          // the card header already shows the report title
          "& .revenue-bar-chart > .MuiTypography-root": { display: "none" },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

/**
 * Chat report body: donut (the real PieChartView), summary chips, data table.
 *   rows / columns / selectedTab — same shapes the real report pages build
 *   summary — { totalBookings, ... } (tabs 2 & 5) or [{ label, value }] (tab 4)
 */
export default function ReportChartView({ rows, columns, selectedTab, summary }) {
  const [allRows, setAllRows] = useState(false);

  if (!rows || rows.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography color="text.secondary" fontSize={13}>
          No data found for this period.
        </Typography>
      </Box>
    );
  }

  const chips = buildSummaryChips(selectedTab, summary);
  const pieColumns =
    selectedTab === 4 ? columns.map((c) => ({ ...c, headerName: shortLegend(c.headerName) })) : columns;
  const visibleRows = allRows ? rows : rows.slice(0, MAX_ROWS_COLLAPSED);
  const firstField = columns[0]?.field;

  return (
    <Box sx={{ boxSizing: "border-box", width: "100%", p: 1.5 }}>
      <FitToWidth>
        <PieChartView data={rows} columns={pieColumns} selectedTab={selectedTab} />
      </FitToWidth>

      {chips.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `repeat(${Math.min(chips.length, 3)}, minmax(0, 1fr))`,
            gap: 1,
            mt: 1.5,
          }}
        >
          {chips.map((c) => (
            <Box
              key={c.label}
              sx={{
                boxSizing: "border-box",
                bgcolor: "#f8fafc",
                border: "1px solid #eef2f6",
                borderRadius: 2,
                px: 1,
                py: 0.75,
                textAlign: "center",
                minWidth: 0,
              }}
            >
              <Typography sx={{ fontSize: 10, color: "#94a3b8", lineHeight: 1.25 }} noWrap title={c.label}>
                {c.label}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a", lineHeight: 1.5 }} noWrap>
                {c.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* All columns, scrolls sideways inside its own box; first column stays put */}
      <Box sx={{ mt: 1.5, border: "1px solid #eef2f6", borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                {columns.map((c, ci) => (
                  <th key={c.field} style={{ ...cellBase, ...headCell, ...(ci === 0 ? stickyCol("#f8fafc") : null) }}>
                    {c.headerName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r, i) => {
                const isTotal = String(r[firstField] ?? "").toLowerCase() === "total";
                const bg = isTotal ? "#eff6ff" : i % 2 ? "#fafbfc" : "#fff";
                return (
                  <tr key={r.id ?? i} style={{ background: bg }}>
                    {columns.map((c, ci) => (
                      <td
                        key={c.field}
                        style={{ ...cellBase, fontWeight: isTotal ? 700 : 400, ...(ci === 0 ? stickyCol(bg) : null) }}
                      >
                        {fmtCell(r[c.field])}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      </Box>

      {rows.length > MAX_ROWS_COLLAPSED && (
        <Stack direction="row" justifyContent="center" mt={1}>
          <Button size="small" onClick={() => setAllRows((v) => !v)} sx={linkBtn}>
            {allRows ? "Show fewer rows" : `Show all ${rows.length} rows`}
          </Button>
        </Stack>
      )}
    </Box>
  );
}

const cellBase = { padding: "6px 10px", borderBottom: "1px solid #f1f5f9", color: "#0f172a", whiteSpace: "nowrap", textAlign: "left" };
const headCell = { background: "#f8fafc", color: "#64748b", fontWeight: 600, borderBottom: "1px solid #e2e8f0" };
const stickyCol = (bg) => ({ position: "sticky", left: 0, background: bg, zIndex: 1 });

const linkBtn = {
  textTransform: "none",
  fontSize: 12,
  fontWeight: 600,
  color: "#2563eb",
  bgcolor: "#eff6ff",
  borderRadius: 2,
  px: 1.5,
  "&:hover": { bgcolor: "#dbeafe" },
};

function buildSummaryChips(selectedTab, summary) {
  if (!summary) return [];
  if (Array.isArray(summary)) return summary;
  if (selectedTab === 2) {
    return [
      { label: "Bookings", value: String(summary.totalBookings ?? 0) },
      { label: "Revenue", value: `$${(summary.totalRevenue ?? 0).toFixed(2)}` },
      { label: "Tax", value: `$${(summary.totalTax ?? 0).toFixed(2)}` },
    ];
  }
  if (selectedTab === 5) {
    return [
      { label: "Bookings", value: String(summary.totalBookings ?? 0) },
      { label: "Commission", value: `$${(summary.totalCommission ?? 0).toFixed(2)}` },
    ];
  }
  return [];
}